import { CommonModule } from '@angular/common';
import {
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
  EventEmitter,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { Subscription } from 'rxjs';
import { WalletDataService } from 'src/app/services/data/wallet-data.service';
import { Wallet } from 'src/app/services/models/wallet-service/wallet.model';
import { LocalStorageService } from 'src/app/services/storage/local-storage.service';
import { isAlert30DaysWallet } from 'src/app/helpers/plan-utils';

const LOADING_WALLETS_OPTION: Wallet = {
  name: $localize`:@@loadingWallets:Cargando carteras...`,
  id: null,
} as any;

@Component({
  selector: 'app-wallet-select',
  templateUrl: './wallet-select.component.html',
  styleUrls: ['./wallet-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => WalletSelectComponent),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
  ],
})
export class WalletSelectComponent implements OnInit, OnDestroy, ControlValueAccessor {
  @Input() id?: string;
  @Input() showWalletTypeIcon = false;
  @Input() disabled = false;
  @Input() enableAutoSelect = false;
  @Input() label = $localize`:@@wallet:Cartera:`;
  @Input() fieldClass = '';
  @Input() dataTestId = 'wallet-select';
  @Input() excludeAlert30Days = false;

  @Output() selectionChange = new EventEmitter<MatSelectChange>();
  @Output() ngModelChange = new EventEmitter<Wallet>();

  walletsList: Wallet[] = [];
  isLoadingWallets: boolean = false;
  selectedWallet: Wallet | null | undefined;

  protected urlAssets: string;
  private readonly subscriptions: Subscription[] = [];

  private onChange: (value: Wallet | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(
    private readonly walletDataService: WalletDataService,
    private readonly localStorageService: LocalStorageService,
  ) {
    this.urlAssets = this.localStorageService.getItem('urlAssets') ?? '';
  }

  ngOnInit() {
    this.subscriptions.push(
      this.walletDataService.getIsLoading$().subscribe((isLoading) => {
        this.isLoadingWallets = isLoading;

        if (isLoading) {
          this.walletsList = [LOADING_WALLETS_OPTION];
          this.selectedWallet = LOADING_WALLETS_OPTION;
        }
      }),
    );

    this.subscriptions.push(
      this.walletDataService.getWallets$().subscribe((wallets) => {
        if (!this.isLoadingWallets) {
          const filtered = this.excludeAlert30Days
            ? wallets.filter(
                (w) => !isAlert30DaysWallet((w as Wallet & { planId?: string }).planId),
              )
            : wallets;

          this.walletsList = filtered;

          const isSelectedInFiltered =
            this.selectedWallet && filtered.some((w) => w.id === this.selectedWallet.id);

          if ((this.selectedWallet && !isSelectedInFiltered) || this.selectedWallet?.id === null) {
            const previousSelectedWallet = this.selectedWallet;
            this.selectedWallet = undefined;
            this.onChange(null);

            if (previousSelectedWallet) {
              this.selectionChange.emit({ value: null } as MatSelectChange);
            }
          }

          if (this.enableAutoSelect && !this.selectedWallet && filtered.length > 0) {
            this.selectWallet(filtered[0]);
            this.selectionChange.emit({ value: filtered[0] } as MatSelectChange);
          }
        }
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  onWalletSelect(event: MatSelectChange) {
    this.selectWallet(event.value);
    this.selectionChange.emit(event);
  }

  private selectWallet(wallet: Wallet | null) {
    this.selectedWallet = wallet;
    this.onChange(wallet);
    this.onTouched();
    if (wallet) {
      this.ngModelChange.emit(wallet);
    }
  }

  getSelectOptionImage(walletType?: string): string {
    if (!walletType) {
      return `${this.urlAssets}/icons/persona-select.png`;
    }

    return walletType === 'COMPANY'
      ? `${this.urlAssets}/icons/company-select.png`
      : `${this.urlAssets}/icons/persona-select.png`;
  }

  compareWallets(w1: Wallet | null, w2: Wallet | null): boolean {
    return w1?.id === w2?.id;
  }

  writeValue(value: Wallet | null): void {
    this.selectedWallet = value;
  }

  registerOnChange(fn: (value: Wallet | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
}
